import {commonTranslations} from "@/utils/translations";
import {MenuItem, Select, SelectProps} from "@mui/material";
import {FC, useCallback} from "react";
import {FormattedMessage} from "react-intl";

type LanguageSelectorProps = Omit<SelectProps<string>, "renderValue" | "onChange"> & {
    languages: string[];
    onChange: (language: string) => void;
};

/**
 * A dropdown selector for abstracting away the generation of options and dynamic FormattedMessage usage.
 * Use it the same way as a normal [Select](https://mui.com/material-ui/api/select/), but instead of
 * passing children, pass an array of language codes as the `languages` prop.
 * Allow [displayEmpty] prop to allow selecting the system default (empty string).
 * */
const LanguageSelector: FC<LanguageSelectorProps> = ({languages, onChange, ...selectProps}) => {
    const renderValue = useCallback((value: string) => {
        // checking for "" and not falsy, to display invalid values like null, undefined or "0"
        // to catch errors earlier
        if (value === "") return <FormattedMessage {...commonTranslations.systemDefault} />;

        /* eslint-disable-next-line formatjs/enforce-default-message, formatjs/enforce-id */
        return <FormattedMessage id={`languages.${value}`} />;
    }, []);

    // if displayEmpty is set, add an empty option for "System Default"
    const languagesToDisplay = selectProps.displayEmpty ? ["", ...languages] : languages;

    return (
        <Select
            renderValue={renderValue}
            onChange={(event) => onChange(event.target.value)}
            {...(selectProps as SelectProps<string>)}
        >
            {languagesToDisplay.map((lang) => (
                <MenuItem key={lang} value={lang}>
                    {renderValue(lang)}
                </MenuItem>
            ))}
        </Select>
    );
};

export default LanguageSelector;
